import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Camera, Upload, X, Trash2, FileText, Shirt, ShieldCheck,
  AlertTriangle, Sparkles, Save, Send, CheckCircle2, Clock, XCircle,
  Plus, MapPin, Calendar, User, Star,
} from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";

/**
 * Single-page authoring wizard for a Supervision Report.
 *
 * UX choice: instead of a multi-step click-through wizard, the form is a
 * single long-scroll page with collapsible sections + autosave-on-blur.
 * Mobile-friendly, fast, and lets the employee jump around as needed.
 *
 * Routes:
 *   /funcionario/relatorios/novo/:bookingId   → creates a draft + redirects
 *   /funcionario/relatorios/:id               → opens existing draft
 */

const SECTION_LABELS = {
  living: "Sala / Áreas Comuns",
  kitchen: "Cozinha",
  bedroom: "Quartos",
  wc: "Casas de Banho",
};

const STATE_OPTIONS = [
  { key: "done",    label: "Concluído", color: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300", iconColor: "text-emerald-400", Icon: CheckCircle2 },
  { key: "pending", label: "Pendente",  color: "bg-[#1a4640]/40 border-[#1a4640] text-[#a0b2af]",        iconColor: "text-[#a0b2af]",   Icon: Clock },
  { key: "issue",   label: "Problema",  color: "bg-red-500/15 border-red-500/40 text-red-300",            iconColor: "text-red-400",     Icon: XCircle },
];

const FINAL_STATES = [
  { key: "ready",              label: "Pronta para Check-in", color: "border-emerald-500/40 hover:bg-emerald-500/10 data-[active=true]:bg-emerald-500/20 data-[active=true]:border-emerald-500" },
  { key: "needs_maintenance",  label: "Necessita Manutenção", color: "border-amber-500/40 hover:bg-amber-500/10 data-[active=true]:bg-amber-500/20 data-[active=true]:border-amber-500" },
  { key: "needs_review",       label: "Necessita Revisão",    color: "border-orange-500/40 hover:bg-orange-500/10 data-[active=true]:bg-orange-500/20 data-[active=true]:border-orange-500" },
  { key: "urgent_issue",       label: "Problema Urgente",     color: "border-red-500/40 hover:bg-red-500/10 data-[active=true]:bg-red-500/20 data-[active=true]:border-red-500" },
];

const RATINGS = [
  { key: "excellent",        label: "Excelente",         icon: "★★★" },
  { key: "good",             label: "Boa",               icon: "★★" },
  { key: "needs_correction", label: "Necessita Correção", icon: "★" },
];

const PHOTO_CATS = [
  { key: "before", label: "Antes",      Icon: Camera },
  { key: "after",  label: "Depois",     Icon: Sparkles },
  { key: "damage", label: "Danos",      Icon: AlertTriangle },
  { key: "final",  label: "Supervisão final", Icon: ShieldCheck },
];

const OCC_TYPES = [
  { key: "damage",       label: "Dano" },
  { key: "lost_found",   label: "Objeto esquecido" },
  { key: "maintenance",  label: "Manutenção" },
  { key: "humidity",     label: "Humidade" },
  { key: "issue",        label: "Problema" },
  { key: "other",        label: "Outro" },
];


function Section({ title, icon, children, testId, action }) {
  return (
    <section data-testid={testId}
             className="rounded-2xl border border-[#1a4640] bg-[#062c27] overflow-hidden">
      <header className="flex items-center justify-between gap-3 px-5 sm:px-6 py-4 border-b border-[#1a4640]">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="text-[#d4af37] shrink-0">{icon}</span>
          <h2 className="font-display text-base sm:text-lg text-[#fdfbf7] truncate">{title}</h2>
        </div>
        {action}
      </header>
      <div className="p-5 sm:p-6">{children}</div>
    </section>
  );
}


function PropertyEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...(data || {}), [k]: v });
  const p = data || {};
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Nome da propriedade" value={p.name} onChange={v => set("name", v)} testId="prop-name" />
      <Field label="Código" value={p.code} onChange={v => set("code", v)} testId="prop-code" />
      <Field label="Morada" value={p.address} onChange={v => set("address", v)} testId="prop-address" full />
      <Field label="Data do serviço" type="date" value={p.service_date} onChange={v => set("service_date", v)} testId="prop-date" />
      <Field label="Hora início" type="time" value={p.start_time} onChange={v => set("start_time", v)} testId="prop-start" />
      <Field label="Hora conclusão" type="time" value={p.end_time} onChange={v => set("end_time", v)} testId="prop-end" />
      <Field label="Duração (min)" type="number" value={p.duration_min} onChange={v => set("duration_min", v ? Number(v) : null)} testId="prop-duration" />
      <Field label="Funcionário responsável" value={p.employee_name} onChange={v => set("employee_name", v)} testId="prop-employee" />
      <Field label="Supervisor responsável" value={p.supervisor_name} onChange={v => set("supervisor_name", v)} testId="prop-supervisor" />
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId, full }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="block text-[10px] uppercase tracking-wider text-[#a0b2af] mb-1.5">{label}</span>
      <input
        data-testid={testId}
        type={type}
        value={value ?? ""}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-md bg-[#041f1b] border border-[#1a4640] text-sm text-[#fdfbf7] placeholder:text-[#a0b2af] focus:border-[#d4af37] focus:outline-none transition-colors"
      />
    </label>
  );
}


function ChecklistEditor({ checklist, onChange }) {
  const sections = Object.keys(SECTION_LABELS);
  const [active, setActive] = useState(sections[0]);
  const items = (checklist?.[active] || []);

  function setItemState(idx, state) {
    const next = { ...checklist };
    next[active] = items.map((it, i) => i === idx ? { ...it, state } : it);
    onChange(next);
  }
  function setItemNote(idx, note) {
    const next = { ...checklist };
    next[active] = items.map((it, i) => i === idx ? { ...it, note } : it);
    onChange(next);
  }
  function quickFillSection(state) {
    const next = { ...checklist };
    next[active] = items.map(it => ({ ...it, state }));
    onChange(next);
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-5">
        {sections.map(k => {
          const total = (checklist?.[k] || []).length;
          const done = (checklist?.[k] || []).filter(it => it.state === "done").length;
          const issues = (checklist?.[k] || []).filter(it => it.state === "issue").length;
          const isActive = k === active;
          return (
            <button key={k}
                    data-testid={`checklist-tab-${k}`}
                    onClick={() => setActive(k)}
                    className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-medium border transition-all ${
                      isActive
                        ? "bg-[#d4af37] text-[#041f1b] border-[#d4af37]"
                        : "bg-[#041f1b] border-[#1a4640] text-[#a0b2af] hover:border-[#d4af37]/50 hover:text-[#fdfbf7]"
                    }`}>
              {SECTION_LABELS[k]}
              <span className={`ml-1 text-[10px] ${isActive ? "text-[#041f1b]/70" : "text-[#a0b2af]"}`}>
                {done}/{total}
              </span>
              {issues > 0 && (
                <span className="ml-1 inline-flex items-center justify-center w-4 h-4 text-[9px] rounded-full bg-red-500/20 text-red-400 font-bold">
                  {issues}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex justify-end gap-2 mb-4">
        <button onClick={() => quickFillSection("done")}
                data-testid="checklist-fill-done"
                className="text-[10px] uppercase tracking-wider text-emerald-400 hover:text-emerald-300 px-2 py-1">
          Marcar tudo concluído
        </button>
        <button onClick={() => quickFillSection("pending")}
                data-testid="checklist-reset"
                className="text-[10px] uppercase tracking-wider text-[#a0b2af] hover:text-[#fdfbf7] px-2 py-1">
          Reset
        </button>
      </div>

      <ul className="space-y-2.5">
        {items.map((it, idx) => {
          const stateOpt = STATE_OPTIONS.find(s => s.key === it.state) || STATE_OPTIONS[1];
          return (
            <li key={idx}
                data-testid={`checklist-item-${active}-${idx}`}
                className={`rounded-lg border p-3 transition-all ${stateOpt.color}`}>
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-[#fdfbf7] flex-1 min-w-0">{it.label}</span>
                <div className="flex gap-1 shrink-0">
                  {STATE_OPTIONS.map(opt => {
                    const Icon = opt.Icon;
                    const active = opt.key === it.state;
                    return (
                      <button key={opt.key}
                              onClick={() => setItemState(idx, opt.key)}
                              data-testid={`checklist-state-${active ? "active-" : ""}${opt.key}`}
                              title={opt.label}
                              className={`p-1.5 rounded-md transition-all ${
                                active
                                  ? "bg-[#fdfbf7]/10 ring-1 ring-current"
                                  : "opacity-40 hover:opacity-100"
                              }`}>
                        <Icon size={14} className={opt.iconColor} />
                      </button>
                    );
                  })}
                </div>
              </div>
              {it.state === "issue" && (
                <input
                  type="text"
                  value={it.note || ""}
                  onChange={e => setItemNote(idx, e.target.value)}
                  placeholder="Descreva o problema…"
                  className="mt-2 w-full px-2.5 py-1.5 text-xs rounded-md bg-[#041f1b] border border-red-500/40 text-[#fdfbf7] placeholder:text-[#a0b2af] focus:border-red-400 focus:outline-none"
                />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}


function PhotosManager({ reportId, photos, onChange, status }) {
  const [activeCat, setActiveCat] = useState("before");
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const fileRef = useRef(null);
  const list = (photos || []).filter(p => p.category === activeCat);
  const editable = status === "draft" || status === "submitted";

  async function uploadFiles(files) {
    if (!editable) return;
    if (!files || files.length === 0) return;
    setUploading(true);
    let ok = 0;
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        if (caption) fd.append("caption", caption);
        await api.post(
          `/supervision-reports/${reportId}/photos?category=${activeCat}`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        ok++;
      } catch (e) {
        toast.error(formatApiError(e, `Erro ao enviar ${file.name}`));
      }
    }
    setUploading(false);
    setCaption("");
    if (ok > 0) {
      toast.success(`${ok} fotografia${ok === 1 ? "" : "s"} carregada${ok === 1 ? "" : "s"}.`);
      onChange();
    }
  }

  async function deletePhoto(p) {
    if (!confirm("Eliminar esta fotografia?")) return;
    try {
      await api.delete(`/supervision-reports/${reportId}/photos/${p.id}`);
      onChange();
    } catch (e) {
      toast.error(formatApiError(e, "Erro ao eliminar"));
    }
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        {PHOTO_CATS.map(c => {
          const Icon = c.Icon;
          const active = c.key === activeCat;
          const count = (photos || []).filter(p => p.category === c.key).length;
          return (
            <button key={c.key}
                    onClick={() => setActiveCat(c.key)}
                    data-testid={`photo-tab-${c.key}`}
                    className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-medium border transition-all ${
                      active
                        ? "bg-[#d4af37] text-[#041f1b] border-[#d4af37]"
                        : "bg-[#041f1b] border-[#1a4640] text-[#a0b2af] hover:border-[#d4af37]/50 hover:text-[#fdfbf7]"
                    }`}>
              <Icon size={12} /> {c.label}
              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${active ? "bg-[#041f1b]/20 text-[#041f1b]" : "bg-[#062c27] text-[#a0b2af]"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {editable && (
        <div className="rounded-lg border border-dashed border-[#1a4640] bg-[#041f1b] p-4 mb-4">
          <input
            type="text"
            value={caption}
            onChange={e => setCaption(e.target.value)}
            placeholder="Legenda opcional aplicada às próximas fotos…"
            className="w-full px-3 py-2 mb-3 text-sm rounded-md bg-[#062c27] border border-[#1a4640] text-[#fdfbf7] placeholder:text-[#a0b2af] focus:border-[#d4af37] focus:outline-none"
          />
          <input
            ref={fileRef}
            type="file"
            multiple
            accept="image/*"
            data-testid="photo-input"
            onChange={e => {
              uploadFiles(e.target.files);
              e.target.value = "";
            }}
            className="hidden"
          />
          <button onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="photo-upload-btn"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-semibold uppercase tracking-wider bg-[#d4af37] text-[#041f1b] hover:bg-[#e8c860] hover:shadow-lg hover:shadow-[#d4af37]/40 transition-all disabled:opacity-50">
            <Upload size={16} />
            {uploading ? "A carregar…" : `Adicionar fotografias · ${PHOTO_CATS.find(c => c.key === activeCat).label}`}
          </button>
          <p className="mt-2 text-[10px] text-center text-[#a0b2af]">
            Pode selecionar várias fotos de uma vez. Sem limite.
          </p>
        </div>
      )}

      {list.length === 0 ? (
        <div className="py-10 text-center text-sm text-[#a0b2af] border border-dashed border-[#1a4640] rounded-xl">
          Sem fotografias nesta categoria.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {list.map(p => (
            <div key={p.id} className="group relative aspect-square rounded-lg overflow-hidden bg-[#041f1b] border border-[#1a4640]">
              <img src={`${BACKEND_URL}/api/files/${p.storage_path}`}
                   alt={p.caption || ""}
                   loading="lazy"
                   className="w-full h-full object-cover" />
              {p.caption && (
                <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                  <p className="text-[10px] text-white line-clamp-2">{p.caption}</p>
                </div>
              )}
              {editable && (
                <button onClick={() => deletePhoto(p)}
                        data-testid={`photo-delete-${p.id}`}
                        className="absolute top-1.5 right-1.5 p-1.5 rounded-full bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function LaundryEditor({ data, onChange }) {
  const d = data || {};
  const set = (k, v) => onChange({ ...d, [k]: v });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Toggle testId="laundry-collected" label="Roupa recolhida" value={d.collected} onChange={v => set("collected", v)} />
        <Toggle testId="laundry-delivered" label="Roupa entregue"  value={d.delivered} onChange={v => set("delivered", v)} />
        <Toggle testId="laundry-towels"    label="Toalhas verificadas" value={d.towels_verified} onChange={v => set("towels_verified", v)} />
      </div>
      <Field label="Quantidade de conjuntos" type="number" value={d.sets_count}
             onChange={v => set("sets_count", v ? Number(v) : null)} testId="laundry-sets" />
      <label className="block">
        <span className="block text-[10px] uppercase tracking-wider text-[#a0b2af] mb-1.5">Roupa danificada / observações</span>
        <textarea
          data-testid="laundry-damaged"
          value={d.damaged_items || ""}
          onChange={e => set("damaged_items", e.target.value)}
          rows={2}
          className="w-full px-3 py-2 rounded-md bg-[#041f1b] border border-[#1a4640] text-sm text-[#fdfbf7] focus:border-[#d4af37] focus:outline-none"
          placeholder="Identificar peças danificadas, em falta…"
        />
      </label>
    </div>
  );
}


function Toggle({ label, value, onChange, testId }) {
  return (
    <button type="button"
            onClick={() => onChange(!value)}
            data-testid={testId}
            className={`flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-lg border transition-all text-left ${
              value
                ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300"
                : "bg-[#041f1b] border-[#1a4640] text-[#a0b2af] hover:border-[#d4af37]/40"
            }`}>
      <span className="text-sm">{label}</span>
      <span className={`w-9 h-5 rounded-full relative transition-colors ${value ? "bg-emerald-500" : "bg-[#1a4640]"}`}>
        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${value ? "left-[18px]" : "left-0.5"}`} />
      </span>
    </button>
  );
}


function SupervisionEditor({ data, onChange }) {
  const d = data || {};
  const set = (k, v) => onChange({ ...d, [k]: v });
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Toggle testId="sup-quality" label="Qualidade aprovada" value={d.quality_ok} onChange={v => set("quality_ok", v)} />
        <Toggle testId="sup-org" label="Organização aprovada" value={d.organization_ok} onChange={v => set("organization_ok", v)} />
        <Toggle testId="sup-visual" label="Padrão visual aprovado" value={d.visual_ok} onChange={v => set("visual_ok", v)} />
        <Toggle testId="sup-equip" label="Equipamentos funcionais" value={d.equipment_ok} onChange={v => set("equipment_ok", v)} />
        <Toggle testId="sup-checkin" label="Pronta para Check-in" value={d.ready_for_checkin} onChange={v => set("ready_for_checkin", v)} />
      </div>
      <div>
        <span className="block text-[10px] uppercase tracking-wider text-[#a0b2af] mb-2">Avaliação</span>
        <div className="flex flex-wrap gap-2">
          {RATINGS.map(r => (
            <button key={r.key}
                    onClick={() => set("rating", d.rating === r.key ? null : r.key)}
                    data-testid={`sup-rating-${r.key}`}
                    className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-medium border transition-all ${
                      d.rating === r.key
                        ? "bg-[#d4af37] text-[#041f1b] border-[#d4af37]"
                        : "bg-[#041f1b] border-[#1a4640] text-[#a0b2af] hover:border-[#d4af37]/50"
                    }`}>
              <Star size={12} /> {r.label}
            </button>
          ))}
        </div>
      </div>
      <label className="block">
        <span className="block text-[10px] uppercase tracking-wider text-[#a0b2af] mb-1.5">Observação do supervisor</span>
        <textarea
          data-testid="sup-note"
          value={d.note || ""}
          onChange={e => set("note", e.target.value)}
          rows={2}
          className="w-full px-3 py-2 rounded-md bg-[#041f1b] border border-[#1a4640] text-sm text-[#fdfbf7] focus:border-[#d4af37] focus:outline-none"
          placeholder="Notas adicionais da supervisão…"
        />
      </label>
    </div>
  );
}


function OccurrencesEditor({ items, onChange }) {
  const list = items || [];
  function update(idx, patch) {
    onChange(list.map((o, i) => i === idx ? { ...o, ...patch } : o));
  }
  function add() {
    onChange([...list, { type: "issue", description: "", severity: "low" }]);
  }
  function remove(idx) {
    onChange(list.filter((_, i) => i !== idx));
  }
  return (
    <div className="space-y-3">
      {list.length === 0 && (
        <p className="text-sm text-[#a0b2af]">Sem ocorrências. Adicione abaixo se houver algo a reportar.</p>
      )}
      {list.map((o, idx) => (
        <div key={idx}
             data-testid={`occurrence-${idx}`}
             className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
          <div className="flex flex-wrap items-start gap-3">
            <select
              data-testid={`occurrence-${idx}-type`}
              value={o.type || "issue"}
              onChange={e => update(idx, { type: e.target.value })}
              className="px-2.5 py-1.5 text-xs rounded-md bg-[#041f1b] border border-[#1a4640] text-[#fdfbf7] focus:border-[#d4af37] focus:outline-none">
              {OCC_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            <select
              data-testid={`occurrence-${idx}-severity`}
              value={o.severity || "low"}
              onChange={e => update(idx, { severity: e.target.value })}
              className="px-2.5 py-1.5 text-xs rounded-md bg-[#041f1b] border border-[#1a4640] text-[#fdfbf7] focus:border-[#d4af37] focus:outline-none">
              <option value="low">Baixa</option>
              <option value="medium">Média</option>
              <option value="high">Alta</option>
            </select>
            <button onClick={() => remove(idx)}
                    data-testid={`occurrence-${idx}-remove`}
                    className="ml-auto p-1.5 rounded-md text-red-400 hover:bg-red-500/10">
              <Trash2 size={14} />
            </button>
          </div>
          <textarea
            data-testid={`occurrence-${idx}-desc`}
            value={o.description || ""}
            onChange={e => update(idx, { description: e.target.value })}
            rows={2}
            placeholder="Descrição da ocorrência…"
            className="mt-2 w-full px-2.5 py-1.5 text-sm rounded-md bg-[#041f1b] border border-amber-500/40 text-[#fdfbf7] focus:border-amber-400 focus:outline-none"
          />
        </div>
      ))}
      <button onClick={add}
              data-testid="occurrence-add"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-xs font-semibold uppercase tracking-wider border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10 transition-all">
        <Plus size={14} /> Adicionar ocorrência
      </button>
    </div>
  );
}


/**
 * Live cleaning timer — counts UP from the moment this report was created
 * (= the moment the employee started the cleaning) and refreshes every
 * second. Persistent: closing & reopening the page keeps the same elapsed
 * time, because the source of truth (`started_at`) is server-side.
 */
function LiveCleaningTimer({ startedAt, expectedMin }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!startedAt) return null;

  const startMs = new Date(startedAt).getTime();
  const elapsedMs = Math.max(0, now - startMs);
  const elapsedMin = Math.floor(elapsedMs / 60000);
  const elapsedSec = Math.floor((elapsedMs % 60000) / 1000);
  const h = Math.floor(elapsedMin / 60);
  const m = elapsedMin % 60;

  // Tone: blue while within expected time, amber if 10% over, red if 25% over.
  const overshootPct = expectedMin ? (elapsedMin / expectedMin - 1) * 100 : 0;
  const tone =
    overshootPct >= 25 ? { ring: "border-red-400/50",    icon: "text-red-300",     value: "text-red-300"     } :
    overshootPct >= 10 ? { ring: "border-amber-400/50",  icon: "text-amber-300",   value: "text-amber-300"   } :
                         { ring: "border-blue-400/50",   icon: "text-blue-300",    value: "text-blue-300"    };

  const startLocal = new Date(startedAt).toLocaleTimeString("pt-PT", {
    hour: "2-digit", minute: "2-digit",
  });

  return (
    <div className={`rounded-2xl border-2 ${tone.ring} bg-[#062c27]/80 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6`}
         data-testid="live-cleaning-timer">
      <div className={`shrink-0 w-14 h-14 rounded-full bg-blue-500/15 ring-2 ring-blue-400/40 grid place-items-center ${tone.icon}`}>
        <Clock size={26} />
      </div>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#a0b2af] font-semibold">
          Em curso há
        </div>
        <div className={`mt-0.5 font-display text-3xl sm:text-4xl ${tone.value}`}
             data-testid="live-cleaning-elapsed">
          {h}h {String(m).padStart(2, "0")}m
          <span className="ml-1 text-base text-[#a0b2af]">{String(elapsedSec).padStart(2, "0")}s</span>
        </div>
      </div>
      <div className="text-xs sm:text-right text-[#a0b2af] leading-relaxed shrink-0">
        Início: <span className="text-[#fdfbf7]">{startLocal}</span><br />
        Duração prevista: <span className="text-[#fdfbf7]">
          {expectedMin >= 60
            ? `${Math.floor(expectedMin / 60)}h ${String(expectedMin % 60).padStart(2, "0")}m`
            : `${expectedMin} min`}
        </span>
      </div>
    </div>
  );
}


export default function EmployeeReportWizard() {
  const { id, bookingId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const lastSaved = useRef(null);

  // 1. Bootstrap: create from booking or load existing
  useEffect(() => {
    let cancelled = false;
    async function init() {
      setLoading(true);
      try {
        if (bookingId) {
          const { data } = await api.post("/supervision-reports", { booking_id: bookingId });
          if (!cancelled) {
            setReport(data);
            navigate(`/funcionario/relatorios/${data.id}`, { replace: true });
          }
        } else if (id) {
          const { data } = await api.get(`/supervision-reports/${id}`);
          if (!cancelled) setReport(data);
        }
      } catch (e) {
        toast.error(formatApiError(e, "Erro ao abrir relatório"));
        navigate("/funcionario/relatorios");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    init();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, bookingId]);

  // 2. Autosave (debounced 800ms after last edit)
  const persist = useCallback(async (patch) => {
    if (!report?.id) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/supervision-reports/${report.id}`, patch);
      setReport(data);
      lastSaved.current = Date.now();
    } catch (e) {
      toast.error(formatApiError(e, "Erro ao guardar"));
    } finally {
      setSaving(false);
    }
  }, [report?.id]);

  const debounceRef = useRef(null);
  function patch(field, value) {
    setReport(prev => ({ ...prev, [field]: value }));
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => persist({ [field]: value }), 800);
  }

  async function reloadPhotos() {
    if (!report?.id) return;
    try {
      const { data } = await api.get(`/supervision-reports/${report.id}`);
      setReport(data);
    } catch (e) {
      // silent
    }
  }

  async function submit() {
    if (!confirm("Submeter este relatório para revisão pelo administrador?")) return;
    setSubmitting(true);
    try {
      // Save any pending edits first
      clearTimeout(debounceRef.current);
      const { data } = await api.post(`/supervision-reports/${report.id}/submit`);
      setReport(data);
      toast.success("Relatório submetido para revisão.");
      navigate("/funcionario/relatorios");
    } catch (e) {
      toast.error(formatApiError(e, "Erro ao submeter"));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading || !report) {
    return (
      <div className="space-y-4" data-testid="wizard-loading">
        <div className="h-10 w-48 rounded bg-[#062c27] animate-pulse" />
        <div className="h-40 rounded-2xl bg-[#062c27] animate-pulse" />
        <div className="h-64 rounded-2xl bg-[#062c27] animate-pulse" />
      </div>
    );
  }

  const editable = report.status === "draft";
  const totalChecklist = Object.values(report.checklist || {}).reduce((acc, arr) => acc + (arr?.length || 0), 0);
  const doneChecklist = Object.values(report.checklist || {}).reduce((acc, arr) => acc + (arr || []).filter(it => it.state === "done").length, 0);
  const totalPhotos = (report.photos || []).length;

  return (
    <div className="space-y-6 pb-32 sm:pb-24" data-testid="employee-report-wizard">
      {/* Top bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => navigate("/funcionario/relatorios")}
                data-testid="wizard-back"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#a0b2af] hover:text-[#d4af37] transition-colors">
          <ArrowLeft size={14} /> Voltar
        </button>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-[#a0b2af] inline-flex items-center gap-1.5">
            {saving ? <><Save size={12} className="animate-pulse text-[#d4af37]" /> A guardar…</>
                    : lastSaved.current ? <><CheckCircle2 size={12} className="text-emerald-400" /> Guardado</>
                    : <><Save size={12} className="text-[#a0b2af]" /> Sem alterações</>}
          </span>
          {!editable && (
            <span className="px-2.5 py-1 rounded-full text-[11px] font-medium border bg-[#3a2f0a] text-[#e8c860] border-[#d4af37]/40">
              {report.status === "submitted" ? "Em revisão" : report.status === "sent" ? "Enviado" : "Aprovado"}
            </span>
          )}
        </div>
      </div>

      {/* Hero / progress */}
      <div className="rounded-2xl border border-[#1a4640] bg-gradient-to-br from-[#062c27] to-[#041f1b] p-6">
        <span className="inline-block text-[10px] uppercase tracking-[0.22em] text-[#d4af37]">
          Relatório de Supervisão
        </span>
        <h1 className="mt-2 font-display text-2xl sm:text-3xl text-[#fdfbf7]">
          {report.property?.name || "Propriedade sem nome"}
        </h1>
        <div className="mt-4 grid grid-cols-3 gap-3 text-center">
          <Stat label="Checklist" value={`${doneChecklist}/${totalChecklist}`} />
          <Stat label="Fotografias" value={totalPhotos} />
          <Stat label="Ocorrências" value={(report.occurrences || []).length} />
        </div>
      </div>

      {/* Live cleaning timer — counts up continuously from the moment the
          report was created (= cleaning start). Survives page reloads
          because `report.started_at` is server-side. */}
      {report.started_at && report.status === "draft" && (
        <LiveCleaningTimer
          startedAt={report.started_at}
          expectedMin={report.expected_duration_min || 75}
        />
      )}

      <Section title="Informação da Propriedade" icon={<MapPin size={18} />} testId="section-property">
        {editable ? (
          <PropertyEditor data={report.property} onChange={v => patch("property", v)} />
        ) : (
          <PropertyEditor data={report.property} onChange={() => {}} />
        )}
      </Section>

      <Section title="Checklist Profissional" icon={<FileText size={18} />} testId="section-checklist">
        <ChecklistEditor checklist={report.checklist} onChange={v => patch("checklist", v)} />
      </Section>

      <Section title="Lavandaria" icon={<Shirt size={18} />} testId="section-laundry">
        <LaundryEditor data={report.laundry} onChange={v => patch("laundry", v)} />
      </Section>

      <Section title="Controlo de Qualidade & Supervisão" icon={<ShieldCheck size={18} />} testId="section-supervision">
        <SupervisionEditor data={report.supervision} onChange={v => patch("supervision", v)} />
      </Section>

      <Section title="Ocorrências e Problemas" icon={<AlertTriangle size={18} />} testId="section-occurrences">
        <OccurrencesEditor items={report.occurrences} onChange={v => patch("occurrences", v)} />
        <label className="block mt-5">
          <span className="block text-[10px] uppercase tracking-wider text-[#a0b2af] mb-1.5">Observações gerais</span>
          <textarea
            data-testid="general-notes"
            value={report.general_notes || ""}
            onChange={e => patch("general_notes", e.target.value)}
            rows={3}
            className="w-full px-3 py-2 rounded-md bg-[#041f1b] border border-[#1a4640] text-sm text-[#fdfbf7] focus:border-[#d4af37] focus:outline-none"
            placeholder="Comentários adicionais…"
          />
        </label>
      </Section>

      <Section title="Registo Fotográfico" icon={<Camera size={18} />} testId="section-photos">
        <PhotosManager
          reportId={report.id}
          photos={report.photos}
          status={report.status}
          onChange={reloadPhotos}
        />
      </Section>

      <Section title="Estado Final" icon={<CheckCircle2 size={18} />} testId="section-final">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {FINAL_STATES.map(s => (
            <button key={s.key}
                    data-active={report.final_state === s.key}
                    onClick={() => patch("final_state", report.final_state === s.key ? null : s.key)}
                    data-testid={`final-${s.key}`}
                    className={`px-4 py-3 rounded-lg border-2 text-sm text-[#fdfbf7] text-left transition-all ${s.color} bg-[#041f1b]`}>
              {s.label}
            </button>
          ))}
        </div>
      </Section>

      {/* Sticky submit bar — sits above the mobile bottom nav (which is
          ~64px tall on phones) so the action button is always visible.
          Layout stacks vertically on phones to keep the CTA full-width. */}
      {editable && (
        <div className="fixed inset-x-0 z-30 bg-[#041f1b]/95 backdrop-blur border-t border-[#1a4640] bottom-[64px] sm:bottom-0">
          <div className="max-w-5xl mx-auto px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
            <span className="hidden sm:inline text-xs text-[#a0b2af]">
              Quando estiver pronto, submeta para o administrador rever e enviar ao cliente.
            </span>
            <button onClick={submit}
                    disabled={submitting}
                    data-testid="wizard-submit"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 sm:py-2.5 rounded-md text-sm sm:text-xs font-semibold uppercase tracking-wider bg-[#d4af37] text-[#041f1b] hover:bg-[#e8c860] hover:shadow-lg hover:shadow-[#d4af37]/40 transition-all disabled:opacity-50">
              <Send size={14} /> {submitting ? "A submeter…" : "Submeter para revisão"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function Stat({ label, value }) {
  return (
    <div className="rounded-xl bg-[#041f1b] border border-[#1a4640] py-3 px-2">
      <div className="font-display text-2xl text-[#d4af37] tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-[#a0b2af] mt-0.5">{label}</div>
    </div>
  );
}
